import { useAuth0 } from '@auth0/auth0-react';
import { Divider, IconButton, ListItemIcon, Menu, MenuItem, Tooltip } from '@mui/material';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Role } from '@/apis/admin-api/types';
import { TrblExitIcon, TrblUserIcon } from '@/components/Icons';
import { BetaBadge } from '@/components/Shared/BetaBadge';
import { useGetMySDKUser } from '@/hooks';

import { menuPaperStyleOverrides } from './utils';

import styles from './styles.module.scss';

export const UserMenu: FC = () => {
  const { user, logout } = useAuth0();
  const { data: sdkUser } = useGetMySDKUser();
  const userRoles = sdkUser?.roles ?? [];
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleUserProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogOutClick = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const gotoUrl = (url: string) => {
    window.open(url, '_blank');
  };

  return (
    <>
      <IconButton className={styles['user-profile-icon']} aria-label="Account profile" onClick={handleUserProfileClick}>
        <TrblUserIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        slotProps={{
          paper: menuPaperStyleOverrides,
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
        <MenuItem className={`${styles['menu-item']} ${styles['info']}`}>
          <ListItemIcon className={styles['menu-icon']}>
            <TrblUserIcon />
          </ListItemIcon>
          {user?.email}
        </MenuItem>
        <Divider />
        {userRoles.includes(Role.Admin) && (
          <Tooltip
            title="Add new users to your existing subscription and get an overview of your subscription and token spend over time"
            arrow
            placement="left">
            <MenuItem onClick={() => navigate('/administration')} className={styles['menu-item']}>
              <ListItemIcon className={styles['menu-icon']}></ListItemIcon>
              Administration
            </MenuItem>
          </Tooltip>
        )}
        <Tooltip title="Set up MCP servers and API keys for AI assistants and MCP clients" arrow placement="left">
          <MenuItem onClick={() => navigate('/mcp')} className={styles['menu-item']}>
            <ListItemIcon className={styles['menu-icon']}></ListItemIcon>
            MCP servers
            <BetaBadge />
          </MenuItem>
        </Tooltip>
        <MenuItem onClick={() => gotoUrl('https://docs.treble.tech/treble-sdk')} className={styles['menu-item']}>
          <ListItemIcon className={styles['menu-icon']}></ListItemIcon>
          Documentation
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogOutClick} className={styles['menu-item']}>
          <ListItemIcon className={styles['menu-icon']}>
            <TrblExitIcon />
          </ListItemIcon>
          Logout
        </MenuItem>
        {userRoles.includes(Role.Superuser) && (
          <span
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              height: '12px',
              fontSize: '8px',
            }}>
            v. {import.meta.env.VITE_APP_VERSION || '0.0.0'}
          </span>
        )}
      </Menu>
    </>
  );
};
